import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Select, LocationSelect, SectorSelect } from '../../../../components/Select'
import { Button } from '../../../../components/Button'
import { Input } from '../../../../components'
import { UserAvatar } from '../../../../components/UserAvatar'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../../components/Forsm/form"
import { sectorFormSchema, type CreateSetorRequest, type SectorFormValues } from './schemas'
import type { User } from '../../../../models'
import { isEquipe } from '../../../../models'
import { FaRegBuilding, FaUsers, FaEnvelope, FaIdBadge, FaGlobeAmericas, FaMapMarkerAlt, FaSitemap } from "react-icons/fa"
import { useCidades } from '../../hooks'
import { useSetores } from '../../../demands/hooks/useData'

interface SectorFormProps {
  usuarios: User[]
  onSubmit: (data: CreateSetorRequest) => Promise<void>
  isLoading?: boolean
  initialData?: {
    id?: number
    nome_setor?: string
    chefe_setor_fk?: number
    cidade_fk?: number
    setor_fk?: number
  } | undefined
  isEdit?: boolean
}

export const SectorForm = ({ 
  usuarios, 
  onSubmit, 
  isLoading = false, 
  initialData,
  isEdit = false 
}: SectorFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()
  
  // Buscar todas as cidades (já vem com estado e país aninhados)
  const { data: cidades, isLoading: isLoadingCidades } = useCidades()
  
  // Buscar todos os setores para o setor superior
  const { setores, loading: isLoadingSetores } = useSetores()

  const form = useForm({
    resolver: zodResolver(sectorFormSchema),
    defaultValues: {
      nome_setor: initialData?.nome_setor || "",
      chefe_setor_fk: initialData?.chefe_setor_fk?.toString() || "",
      cidade_fk: initialData?.cidade_fk?.toString() || "",
      setor_fk: initialData?.setor_fk?.toString() || "",
    },
  })

  // Converter usuários para opções do Select (com formato UserOption)
  const userOptions = usuarios.map(user => ({
    value: user.id.toString(),
    label: user.name,
    name: user.name,
    email: user.email,
    ...(user.avatar && { avatar: user.avatar }),
  }))

  // Opções de setores superiores
  const setorOptions = useMemo(() => {
    if (!setores) return []
    
    // Filtrar apenas setores (não equipes) e excluir o setor atual em edição
    const setoresDisponiveis = setores.filter(setor => 
      !isEquipe(setor) && 
      (!initialData || setor.id !== initialData.id)
    )
    
    return setoresDisponiveis.map(setor => ({
      value: setor.id.toString(),
      label: setor.nome_setor,
      nome_setor: setor.nome_setor,
      ...(setor.cidade?.name && { descricao: setor.cidade.name }),
    }))
  }, [setores, initialData])

  // Opções de cidades com formato LocationOption
  const cidadeOptions = useMemo(() => {
    if (!cidades) return []
    return cidades.map(cidade => ({
      value: cidade.id.toString(),
      label: `${cidade.name} - ${cidade.estado?.name || ''}, ${cidade.estado?.pais?.name || ''}`,
      cidade: cidade.name,
      ...(cidade.estado?.name && { estado: cidade.estado.name }),
      ...(cidade.estado?.pais?.name && { pais: cidade.estado.pais.name }),
    }))
  }, [cidades])

  // Encontrar cidade selecionada
  const selectedCidadeId = form.watch("cidade_fk")
  const selectedCidade = useMemo(() => {
    if (!selectedCidadeId || !cidades) return null
    return cidades.find(c => c.id.toString() === selectedCidadeId) || null
  }, [selectedCidadeId, cidades])

  // Encontrar setor superior selecionado
  const selectedSetorId = form.watch("setor_fk")
  const selectedSetor = useMemo(() => {
    if (!selectedSetorId || !setores) return null
    return setores.find(s => s.id.toString() === selectedSetorId) || null
  }, [selectedSetorId, setores])

  // Encontrar usuário selecionado
  const selectedUserId = form.watch("chefe_setor_fk")
  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null
    return usuarios.find(user => user.id.toString() === selectedUserId) || null
  }, [selectedUserId, usuarios])

  const handleSubmit = async (data: SectorFormValues) => {
    try {
      setIsSubmitting(true)
      
      // Converter para o formato da API
      const requestData: CreateSetorRequest = {
        nome_setor: data.nome_setor,
        cidade_fk: Number(data.cidade_fk),
        ...(data.chefe_setor_fk && data.chefe_setor_fk !== "" && {
          chefe_setor_fk: Number(data.chefe_setor_fk)
        }),
        ...(data.setor_fk && data.setor_fk !== "" && {
          setor_fk: Number(data.setor_fk)
        })
      }

      await onSubmit(requestData)
      
      // Resetar formulário após sucesso
      form.reset()
      navigate('/gestao/setores')
    } catch (error) {
      console.error('Erro ao criar setor:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-white dark:bg-neutral-800 shadow-sm rounded-lg border border-gray-200 dark:border-neutral-700">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-[calc(100vh-206px)]">
        {/* Primeira Coluna - Formulário */}
        <div className="px-6 py-8 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-neutral-600 scrollbar-track-transparent">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
              <FaRegBuilding className="text-blue-600 dark:text-blue-400" />
              {isEdit ? 'Editar Setor' : 'Criar Novo Setor'}
            </h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {isEdit 
                ? 'Modifique as informações do setor conforme necessário.'
                : 'Defina um novo setor organizacional com seu respectivo chefe.'
              }
            </p>
          </div>

          <div className="space-y-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                
                {/* Nome do Setor */}
                <FormField
                  control={form.control}
                  name="nome_setor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome do Setor</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Ex: Recursos Humanos" 
                          {...field} 
                          className="text-base"
                        />
                      </FormControl>
                      <FormDescription>
                        Nome identificador do setor organizacional
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Localização - Cidade */}
                <FormField
                  control={form.control}
                  name="cidade_fk"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Localização</FormLabel>
                      <FormControl>
                        <LocationSelect
                          options={cidadeOptions}
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Selecione a cidade..."
                          searchable
                          disabled={isLoading || isLoadingCidades}
                        />
                      </FormControl>
                      <FormDescription>
                        Cidade onde o setor está localizado
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Setor Superior */}
                <FormField
                  control={form.control}
                  name="setor_fk"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Empresa (Opcional)</FormLabel>
                      <FormControl>
                        <SectorSelect
                          options={setorOptions}
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Selecione o setor superior..."
                          searchable
                          disabled={isLoading || isLoadingSetores}
                        />
                      </FormControl>
                      <FormDescription>
                        Setor ao qual este setor está subordinado
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Chefe do Setor */}
                <FormField
                  control={form.control}
                  name="chefe_setor_fk"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Chefe do Setor</FormLabel>
                      <FormControl>
                        <Select
                          variant="user"
                          options={userOptions}
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Selecione o chefe do setor..."
                          searchable
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormDescription>
                        Usuário responsável pela gestão do setor
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Botões de ação */}
                <div className="pt-6 border-t border-gray-200 dark:border-neutral-700">
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                    <Button 
                      type="button" 
                      variant="outline"
                      className="order-2 sm:order-1"
                      onClick={() => form.reset()}
                      disabled={isSubmitting || isLoading}
                    >
                      Limpar
                    </Button>
                    <Button 
                      type="submit"
                      className="order-1 sm:order-2"
                      disabled={isSubmitting || isLoading}
                    >
                      {isSubmitting 
                        ? (isEdit ? 'Salvando...' : 'Criando...') 
                        : (isEdit ? 'Salvar Alterações' : 'Criar Setor')
                      }
                    </Button>
                  </div>
                </div>

              </form>
            </Form>
          </div>
        </div>

        {/* Segunda Coluna - Informações do Chefe */}
        <div className="px-6 py-8 bg-gradient-to-br from-blue-50 dark:from-blue-900/20 to-indigo-50 dark:to-indigo-900/20 border-l border-blue-100 dark:border-blue-800 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-neutral-600 scrollbar-track-transparent">
          <div className="space-y-6">
            {/* Informações de Localização */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <FaGlobeAmericas className="text-blue-600 dark:text-blue-400" />
                Localização do Setor
              </h3>
              
              <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700 p-4">
                {selectedCidade ? (
                  <div className="flex items-center gap-3">
                    <FaMapMarkerAlt className="text-blue-500 flex-shrink-0 w-5 h-5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {selectedCidade.name} - {selectedCidade.estado?.name}, {selectedCidade.estado?.pais?.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Localização do setor
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <FaGlobeAmericas className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Selecione a localização do setor
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Informações do Setor Superior */}
            {selectedSetor && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <FaSitemap className="text-blue-600 dark:text-blue-400" />
                  Setor Superior
                </h3>
                
                <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700 p-4">
                  <div className="flex items-center gap-3">
                    <FaRegBuilding className="text-indigo-500 flex-shrink-0 w-5 h-5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {selectedSetor.nome_setor}
                      </p>

                      {selectedSetor.cidade?.name && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                          <FaMapMarkerAlt className="w-3 h-3" />
                          {selectedSetor.cidade.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Informações do Chefe */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <FaUsers className="text-blue-600 dark:text-blue-400" />
                Informações do Chefe
              </h3>
            
            <AnimatePresence mode="wait">
              {selectedUser ? (
                <motion.div
                  key={selectedUser.id}
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{
                    duration: 0.3,
                    ease: "easeOut"
                  }}
                  className='bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700 p-6 text-center space-y-4'
                >
                  {/* Avatar centralizado */}
                  <motion.div 
                    className='flex justify-center'
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                  >
                    <div className='relative'>
                      <UserAvatar
                        user={selectedUser}
                        size='xl'
                        showPopup={false}
                        className='shadow-lg border-4 border-white'
                      />
                      <motion.div 
                        className='absolute -bottom-1 -right-1 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center'
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, duration: 0.2, type: "spring", stiffness: 200 }}
                      >
                        <FaRegBuilding className='w-3 h-3' />
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Nome e título centralizados */}
                  <motion.div 
                    className='space-y-2'
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15, duration: 0.3 }}
                  >
                    <h4 className='text-xl font-bold text-gray-900 dark:text-gray-100'>
                      {selectedUser.name}
                    </h4>
                    <p className='text-blue-600 dark:text-blue-400 font-semibold'>
                      Chefe do Setor
                    </p>
                    <p className='text-sm text-gray-500 dark:text-gray-400'>
                      {selectedUser.cargo_usuario}
                    </p>
                  </motion.div>

                  {/* Informações organizadas */}
                  <motion.div 
                    className='space-y-3 pt-4 border-t border-gray-100 dark:border-neutral-700'
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                  >
                    <div className='flex items-center justify-center gap-2 text-sm'>
                      <FaEnvelope className='text-gray-400 w-4 h-4' />
                      <span className='text-gray-600 dark:text-gray-400'>{selectedUser.email}</span>
                    </div>
                    
                    <div className='flex justify-center'>
                      <span className='inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-sm font-medium rounded-full'>
                        <FaIdBadge className='w-3 h-3' />
                        {selectedUser.funcao_usuario}
                      </span>
                    </div>
                  </motion.div>

                  {/* Descrição */}
                  <motion.div 
                    className='bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mt-4'
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25, duration: 0.3 }}
                  >
                    <p className='text-sm text-blue-800 dark:text-blue-300 text-center'>
                      <strong>Este usuário será designado como chefe do setor</strong>
                      <br />
                      <span className='text-blue-700 dark:text-blue-400'>
                        Responsável pela gestão e supervisão da equipe.
                      </span>
                    </p>
                  </motion.div>
                </motion.div>
              ) : (
              <div className="text-center py-8">
                <div className="bg-gray-100 dark:bg-neutral-700 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <FaUsers className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Selecione um usuário para ver as informações do chefe do setor
                </p>
              </div>
              )}
            </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}